import { apiRequest } from '@/lib/queryClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type EvaluateRequest, type GenerateRequest, MODEL_REVIEW_ENDPOINTS } from './api';
import type { EvaluateResponse, GenerateResponse, ModelCatalogResponse, Prompt, Session, Template } from './types';

export const useModelCatalog = () => useQuery<ModelCatalogResponse>({ queryKey: [MODEL_REVIEW_ENDPOINTS.models] });

export const usePrompts = () => useQuery<{ prompts: Prompt[] }>({ queryKey: [MODEL_REVIEW_ENDPOINTS.prompts] });

export const useTemplates = () => useQuery<{ templates: Template[] }>({ queryKey: [MODEL_REVIEW_ENDPOINTS.templates] });

export const useSessions = () => useQuery<Session[]>({ queryKey: [MODEL_REVIEW_ENDPOINTS.sessions] });

/**
 * Keyed by session id, so a slow response for a previous session can no longer
 * overwrite a newer one — the bug the old uncancelled useEffect had.
 *
 * The response also carries the summaries production stored for the session;
 * same request, no extra fetch.
 */
export const useTranscriptPreview = (sessionId: string | null) =>
  useQuery<{
    segments?: { text?: string; speaker_label?: string; start_time?: number }[];
    model_summary?: { summary_text?: string } | null;
    user_summary?: { summary_text?: string } | null;
  }>({
    queryKey: [`/api/sessions/${sessionId}/live-conversation`],
    enabled: Boolean(sessionId),
  });

export const useGenerateComparison = () =>
  useMutation<GenerateResponse, Error, GenerateRequest>({
    mutationFn: async (body) => {
      const res = await apiRequest('POST', MODEL_REVIEW_ENDPOINTS.generate, body);
      return res.json();
    },
  });

export const useEvaluateSummaries = () =>
  useMutation<EvaluateResponse, Error, EvaluateRequest>({
    mutationFn: async (body) => {
      const res = await apiRequest('POST', MODEL_REVIEW_ENDPOINTS.evaluate, body);
      return res.json();
    },
  });
